/**
 * The building view's lift columns, read from W1's schema 2.0 asset
 * (twin slice 1, W6 steps 1, 7, 10 and 12): what each column spans and
 * marks, what its caption says, and the line that says where all of it
 * comes from.
 *
 * Pure and free of three.js, so it is testable without a GPU and the
 * main bundle may read it (the Devices table words a car the same way
 * the columns do). `twin-renderer.ts` turns a spec into geometry.
 *
 * THREE FLOOR LISTS, NEVER CONFUSED. Each car in the asset keeps them
 * apart, and each mark here reads exactly one:
 *
 *   ticks      `served_levels`, the lift CONTROLLER's floors: building
 *              truth, what a guest can ride to. Never the robot map's
 *              cabin visits (`map_levels`): FL2 visits L3 and L4 in the
 *              robot map and serves all 22 floors.
 *   rail       `robot.levels` of a `between_floors` car (SL1): the floors
 *              robots ride it to, broken at every served floor that is
 *              not one of them (L5). No other car gets one.
 *   crossings  `robot.levels` of a `same_floor` car (PL3): the floors on
 *              which robots cross the car door to door. Never its served
 *              floors (17 of them; robots cross on 12).
 *
 * There is no 1.x fallback: the loader rejects 1.x (`checkShape`).
 *
 * WHAT A CAPTION MAY SAY. The car's name, and, when the car cannot be
 * drawn as reporting, why, in the drawer's own words: "No reading since
 * HH:MM", "No fresh reading", "No reading", "Not known: this screen is
 * reconnecting" (copy deck v2, N9), "Not known", "Checking", "Fire
 * service". The time is the row's own stamp, shown as
 * a time of day (`formatClock`), never as an age, and only where it is
 * the time of the last reading (`liftReading`'s rule). Nothing here
 * names a direction, and nothing places a car between floors.
 *
 * Copy here is pending the slice-1 copy review (gate B).
 */
import type { LiftViewModel } from '../../api/adapters';
import type { Audience } from '../../auth/roles';
import type { LiftWriteGate } from '../../api/v1';
import type { LiftCarView, LiftLook } from '../Devices/lift-column-state';
import { liftDispatchStatus } from '../Devices/lift-dispatch-status';
import { LIFT_NO_READING_WORDS, liftNoReadingLine } from '../Devices/lift-reading';
import type { TwinGeometry, TwinLift, TwinLiftGeometry, TwinRobotUse } from './geometry';

// ----------------------------------------------------------------------------
// The column, from the asset.
// ----------------------------------------------------------------------------

/** One floor of a column: the level's name and its elevation in the
 *  asset's ladder. */
export interface ColumnFloor {
  readonly level: string;
  readonly elevation: number;
}

export interface LiftColumnSpec {
  readonly name: string;
  readonly geometry: TwinLiftGeometry;
  readonly use: TwinRobotUse;
  /** A car robots do not use (`robot.use: none`): drawn lighter. */
  readonly guest: boolean;
  /** Placed by hand from the floorplans (`geometry.source: hand`):
   *  drawn with dashed edges. */
  readonly handPlaced: boolean;
  /** One tick per controller-served floor the asset has, lowest first. */
  readonly served: readonly ColumnFloor[];
  /** The floors robots ride a `between_floors` car over, lowest first;
   *  empty for every other car. */
  readonly rail: readonly ColumnFloor[];
  /** The floors robots cross a `same_floor` car on, lowest first; empty
   *  for every other car. */
  readonly crossings: readonly ColumnFloor[];
}

/** The floors of `names` the ladder has, lowest first. A name the
 *  ladder does not have is left out rather than guessed. */
function floorsOf(
  names: readonly string[],
  elevations: ReadonlyMap<string, number>,
): ColumnFloor[] {
  const out: ColumnFloor[] = [];
  for (const level of names) {
    const elevation = elevations.get(level);
    if (typeof elevation === 'number') out.push({ level, elevation });
  }
  return out.sort((a, b) => a.elevation - b.elevation);
}

export function liftColumnSpec(
  lift: TwinLift,
  elevations: ReadonlyMap<string, number>,
): LiftColumnSpec {
  const use = lift.robot.use;
  const robotFloors = floorsOf(lift.robot.levels, elevations);
  return {
    name: lift.name,
    geometry: lift.geometry,
    use,
    guest: use === 'none',
    handPlaced: lift.geometry.source === 'hand',
    served: floorsOf(lift.served_levels, elevations),
    rail: use === 'between_floors' ? robotFloors : [],
    crossings: use === 'same_floor' ? robotFloors : [],
  };
}

/** The cars this build draws: those placed from the robot map.
 *
 *  PL1 and PL2 were placed by hand from the floorplans, to within
 *  `precision_units`. Until that placement is signed off (gate E) they are
 *  not drawn at all, column or car: a hand-placed box claims a precision
 *  nobody has checked. `buildLiftColumn` already draws such a car dashed,
 *  for the day this filter goes. */
export function drawnLifts(geometry: Pick<TwinGeometry, 'lifts'>): readonly TwinLift[] {
  return geometry.lifts.filter((lift) => lift.geometry.source === 'map');
}

/** The ladder: level name to elevation. */
export function levelElevations(geometry: Pick<TwinGeometry, 'levels'>): Map<string, number> {
  return new Map(geometry.levels.map((l) => [l.name, l.elevation]));
}

/** A spec for each of `lifts` (every car in the asset by default), in
 *  the asset's order. */
export function liftColumnSpecs(
  geometry: Pick<TwinGeometry, 'levels' | 'lifts'>,
  lifts: readonly TwinLift[] = geometry.lifts,
): LiftColumnSpec[] {
  const elevations = levelElevations(geometry);
  return lifts.map((lift) => liftColumnSpec(lift, elevations));
}

/** Pit below the lowest served landing and head above the highest, in
 *  the asset's units. The building is not to scale vertically; these
 *  only keep a column from ending flush with a plate. */
export const COLUMN_PIT = 1;
export const COLUMN_HEAD = 3;

/**
 * The vertical extent of a column, or null when it has no served floor
 * at or below `maxElevation`.
 *
 * `maxElevation` is the focused floor: the storeys above it are cut
 * away, and a column drawn full height would stand through the cut and
 * hide the plan. Clipped, it still shows which cars reach this floor.
 */
export function columnSpan(
  spec: Pick<LiftColumnSpec, 'served'>,
  maxElevation?: number,
): { readonly z0: number; readonly z1: number } | null {
  const served = spec.served.filter(
    (f) => maxElevation === undefined || f.elevation <= maxElevation,
  );
  if (served.length === 0) return null;
  return {
    z0: served[0]!.elevation - COLUMN_PIT,
    z1: served[served.length - 1]!.elevation + COLUMN_HEAD,
  };
}

/**
 * The stretches of a column the rail covers: one per unbroken run of
 * robot floors, lowest first, in the asset's units.
 *
 * BROKEN WHERE ROBOTS DO NOT RIDE. SL1's robot floors are L4, L4M and
 * L6-L17 (contract §0 Q5): L5 is served but is not a robot floor, and
 * one bar from L4 to L17 would claim it as one. So a run is a sequence
 * of robot floors with no other served floor between them, and it
 * reaches half way to the served floor on either side: every robot floor
 * sits inside the rail and every other served floor in a gap. A run at
 * an end of the served list stops at its last floor.
 *
 * `maxElevation` clips it as it clips the column (`columnSpan`): a run
 * that starts above the focused floor is gone, and one that goes on past
 * it stops at it.
 */
export function railSegments(
  spec: Pick<LiftColumnSpec, 'served' | 'rail'>,
  maxElevation?: number,
): { readonly y0: number; readonly y1: number }[] {
  if (spec.rail.length === 0) return [];
  const onRail = new Set(spec.rail.map((f) => f.level));
  // The served floors and the robot floors together, lowest first, so a
  // robot floor the controller roster lacks is still railed.
  const byLevel = new Map<string, ColumnFloor>();
  for (const f of [...spec.served, ...spec.rail]) byLevel.set(f.level, f);
  const floors = [...byLevel.values()].sort((a, b) => a.elevation - b.elevation);

  const out: { y0: number; y1: number }[] = [];
  let i = 0;
  while (i < floors.length) {
    if (!onRail.has(floors[i]!.level)) {
      i += 1;
      continue;
    }
    let j = i;
    while (j + 1 < floors.length && onRail.has(floors[j + 1]!.level)) j += 1;
    const first = floors[i]!.elevation;
    const last = floors[j]!.elevation;
    const below = floors[i - 1];
    const above = floors[j + 1];
    i = j + 1;
    if (maxElevation !== undefined && first > maxElevation) continue;
    const y0 = below ? (below.elevation + first) / 2 : first;
    const y1 = above ? (last + above.elevation) / 2 : last;
    out.push({ y0, y1: maxElevation === undefined ? y1 : Math.min(y1, maxElevation) });
  }
  return out;
}

// ----------------------------------------------------------------------------
// The car's live label.
// ----------------------------------------------------------------------------

export const NOT_KNOWN = 'Not known';
export const CHECKING = 'Checking';
export const FIRE_SERVICE = 'Fire service';

/** The row fields the label reads. */
export type LiftLabelRow = Pick<LiftViewModel, 'lastSeen' | 'mode' | 'fault' | 'hasLiveState'>;

/**
 * What a car's caption adds to its name, or null when the car is drawn
 * as reporting and has nothing to add.
 *
 * A car with no reading this page trusts is said in the drawer's and the
 * cards' own words (`liftNoReadingLine`, one set; copy deck v2, N9):
 *
 *   absent        "No reading": nothing has reported it.
 *   lost          "No reading since HH:MM" when this page stopped seeing
 *                 its row advance (the row's own stamp IS the last
 *                 reading); "No fresh reading" when the connector or
 *                 adapter says the reading is stale (the worker keeps
 *                 stamping the row, so its stamp is not when the reading
 *                 went); "Not known: this screen is reconnecting" when
 *                 THIS PAGE's feed is clamped, which says nothing about
 *                 the lift.
 *   checking      "Checking": seen once, not yet advanced.
 *   off           "Not known" when the car is not placed: a disarmed
 *                 site publishes mode off for every car, link up or
 *                 down. Placed (its telemetry vouched fresh), nothing.
 *   live, quiet   "Fire service" while the car is in it; else nothing.
 *
 * `clock` turns an epoch-ms stamp into a time of day (`formatClock`).
 */
export function liftLiveLabel(
  view: Pick<LiftCarView, 'freshness' | 'level'>,
  row: LiftLabelRow | undefined,
  gate: LiftWriteGate | null | undefined,
  clock: (epochMs: number) => string,
): string | null {
  const { state, lostBecause } = view.freshness;
  switch (state) {
    case 'absent':
      return LIFT_NO_READING_WORDS.noReading;
    case 'lost':
      return liftNoReadingLine(lostBecause, row, clock);
    case 'checking':
      return CHECKING;
    case 'off':
      return view.level === null ? NOT_KNOWN : null;
    case 'live':
    case 'quiet':
      return row && liftDispatchStatus(row, gate).kind === 'fire_service' ? FIRE_SERVICE : null;
  }
}

// ----------------------------------------------------------------------------
// What the canvas draws of each car.
// ----------------------------------------------------------------------------

/** One car as the building view draws it: its look (`LiftLook`, the
 *  same one the 2D map and the drawer read) and its caption's label. */
export interface TwinLiftCar extends LiftLook {
  readonly label: string | null;
}

/**
 * Every car in `views`, with its label, keyed by name. A car's look is
 * passed through untouched: the canvas never reads a raw row.
 */
export function twinLiftCars(
  views: ReadonlyMap<string, LiftCarView>,
  rows: readonly LiftViewModel[] | undefined,
  gate: LiftWriteGate | null | undefined,
  clock: (epochMs: number) => string,
): ReadonlyMap<string, TwinLiftCar> {
  const byName = new Map<string, LiftViewModel>();
  for (const row of rows ?? []) if (!byName.has(row.name)) byName.set(row.name, row);
  const out = new Map<string, TwinLiftCar>();
  for (const [name, view] of views) {
    out.set(name, {
      look: view.look,
      level: view.level,
      door: view.door,
      motion: view.motion,
      label: liftLiveLabel(view, byName.get(name), gate, clock),
    });
  }
  return out;
}

/** A caption's lines, in order: the car's name (with "placed by hand"
 *  for a car so placed, for the day gate E lets one be drawn), then its
 *  live label if it has one. */
export function liftCaptionLines(
  spec: Pick<LiftColumnSpec, 'name' | 'handPlaced'>,
  label: string | null | undefined,
): string[] {
  const lines = [spec.handPlaced ? `${spec.name} · placed by hand` : spec.name];
  if (label) lines.push(label);
  return lines;
}

// ----------------------------------------------------------------------------
// Where the captions go on screen.
// ----------------------------------------------------------------------------

/** One caption to lay out: where its column's top lands on the stage,
 *  in px (the point the caption's bottom edge wants to sit on), and the
 *  caption's own size in px. */
export interface CaptionToLay {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** A caption keeps this far inside the stage's edges, and this far from
 *  any other caption, in px. */
export const CAPTION_MARGIN_PX = 4;
export const CAPTION_GAP_PX = 2;

/**
 * Where each caption's top-left corner goes on the stage, in px, or null
 * for one that is not shown: no anchor (its column top is behind the
 * camera), or an anchor off the stage's sides or below its bottom.
 *
 * WHY THIS EXISTS. The drawn columns stand within a few units of each
 * other, so their tops project a few px apart, and in the whole-building
 * view they land at the stage's top edge. Bottom-anchored over their
 * columns, the captions piled on one another and ran off the top, so one
 * car's "No reading since 14:02" could sit under another car's name, or
 * lose its own car's name above the edge. That is worst in an adapter
 * outage, when every car says "No fresh reading" at once.
 *
 *   1. Each caption is centred over its anchor with its bottom on it,
 *      then moved inside the stage. It moves sideways only for that.
 *   2. Taken from the top of the stage down, each caption that would
 *      overlap one already placed moves to the nearest free spot
 *      directly above or below that caption. The cluster becomes a
 *      stack over the columns, each caption carrying its car's name.
 *
 * A stage too small to hold every caption lets the last ones overlap
 * rather than push them off it.
 */
export function layoutLiftCaptions(
  captions: readonly (CaptionToLay | null)[],
  stage: { readonly width: number; readonly height: number },
): ({ readonly left: number; readonly top: number } | null)[] {
  const M = CAPTION_MARGIN_PX;
  const G = CAPTION_GAP_PX;
  const { width: W, height: H } = stage;
  // `hi < lo` (a caption wider than the stage) pins it to `lo`.
  const within = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(v, hi));

  const wanted: { i: number; left: number; top: number }[] = [];
  captions.forEach((c, i) => {
    if (!c || !Number.isFinite(c.x) || !Number.isFinite(c.y)) return;
    if (c.x < 0 || c.x > W || c.y > H) return;
    wanted.push({
      i,
      left: within(c.x - c.width / 2, M, W - M - c.width),
      top: within(c.y - c.height, M, H - M - c.height),
    });
  });
  wanted.sort((a, b) => a.top - b.top || a.left - b.left || a.i - b.i);

  const out: ({ left: number; top: number } | null)[] = captions.map(() => null);
  const placed: { left: number; top: number; width: number; height: number }[] = [];
  for (const w of wanted) {
    const { width, height } = captions[w.i]!;
    const clear = (top: number) =>
      top >= M &&
      top + height <= H - M &&
      !placed.some(
        (p) =>
          p.left < w.left + width + G &&
          w.left < p.left + p.width + G &&
          p.top < top + height + G &&
          top < p.top + p.height + G,
      );
    const candidates = [
      w.top,
      ...placed.flatMap((p) => [p.top + p.height + G, p.top - G - height]),
    ];
    const free = candidates
      .filter(clear)
      .sort((a, b) => Math.abs(a - w.top) - Math.abs(b - w.top) || b - a);
    const top = free[0] ?? w.top;
    placed.push({ left: w.left, top, width, height });
    out[w.i] = { left: w.left, top };
  }
  return out;
}

// ----------------------------------------------------------------------------
// Where the lift facts come from.
// ----------------------------------------------------------------------------

/**
 * The line that names the lift sources, for the building view's
 * caveats (W6 step 12; W4 carries the same line on the Overview).
 *
 * Read from the asset, not written here: which cars were placed by hand,
 * and whether the view draws them. `freshnessCaveat` is the cars' own
 * caveat (`LiftCarView.caveat`, from `liftFreshnessCaveat`), set while
 * any car's row lacks both of the adapter's freshness measures, already
 * worded for the reader (`liftFreshnessCaveatFor`). It is added as given,
 * so this line, the drawer and the Devices table say it in one sentence.
 */
export function liftProvenanceLine(
  geometry: Pick<TwinGeometry, 'lifts'>,
  opts: {
    readonly drawn: readonly string[];
    readonly freshnessCaveat: string | null;
    /** Ops keep the provenance whole. A site audience gets no internal
     *  decision date, and no "until that placement is checked": a car that
     *  is not drawn is said to be not drawn (copy deck v2, N8). A drawn
     *  hand-placed car is "placed by hand" for everyone: Decision 3's
     *  words, which its dotted caption carries too. */
    readonly audience: Audience;
  },
): string {
  const ops = opts.audience === 'ops';
  const parts = [
    'Lifts: served floors from the lift controller',
    ops
      ? 'robot use from the robot map and the 2026-10-03 decision'
      : 'robot use from the robots’ map',
  ];
  const hand = geometry.lifts.filter((l) => l.geometry.source === 'hand').map((l) => l.name);
  if (hand.length > 0) {
    const names = hand.join(', ');
    const hidden = hand.filter((n) => !opts.drawn.includes(n));
    if (ops) {
      parts.push(
        hidden.length === 0
          ? `${names} placed by hand`
          : `${names} placed by hand, not drawn until that placement is checked`,
      );
    } else {
      const shown = hand.filter((n) => opts.drawn.includes(n));
      if (shown.length > 0) parts.push(`${shown.join(', ')} placed by hand`);
      if (hidden.length > 0) parts.push(`${hidden.join(', ')} not drawn`);
    }
  }
  const line = `${parts.join(' · ')}.`;
  return opts.freshnessCaveat ? `${line} ${opts.freshnessCaveat}` : line;
}
